/**
 * @fileoverview Resend-invitation mutation hook.
 * @description Wraps POST /identity/invitations/{id}/resend in a TanStack mutation. The list is
 * refreshed whatever the outcome: a 409 / 1058 or 404 / 1057 means the invitation changed
 * underneath the admin, so the row should show its current state.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { resendInvitation } from '@/features/user-invitations/services/user-invitations.service'
import { INVITATIONS_QUERY_KEY } from '@/shared/constants/invitations-query-keys.constants'

import type { ResendInvitationResponse } from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Re-sends an invitation.
 * @returns {ReturnType<typeof useMutation<ResendInvitationResponse, ApiError, string>>} The
 * mutation object; mutate it with the invitation id.
 */
export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation<ResendInvitationResponse, ApiError, string>({
    /**
     * @description Re-sends one invitation, passing only its id to the service.
     * @param {string} invitationId - The invitation to re-send.
     * @returns {Promise<ResendInvitationResponse>} The refreshed invitation.
     */
    mutationFn: (invitationId) => resendInvitation(invitationId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })
    },
  })
}
